import { areIntervalsOverlapping } from 'date-fns'
import { useMemo } from 'react'

import type { AppointmentTypeId, ClinicId } from '@snootsvet/shared'
import {
  APPOINTMENT_TYPES_BY_ID,
  addCalendarDays,
  HEALTHY_SUBSCRIPTION_STATUSES,
  isLocumVet,
  wallClockInterval,
} from '@snootsvet/shared'

import { useClinicSchedule } from '@/frontend/features/appointments/use-clinic-schedule'
import { useListAppointments } from '@/frontend/features/appointments/use-list-appointments'
import { useListBlockedSlots } from '@/frontend/features/appointments/use-list-blocked-slots'
import { useGetPatientInvoices } from '@/frontend/hooks/api/patients/use-get-patient-invoices'
import { useGetPatientSubscription } from '@/frontend/hooks/api/patients/use-get-patient-subscription'
import { useListPatientAppointmentTypes } from '@/frontend/hooks/api/patients/use-list-patient-appointment-types'
import { useListStaff } from '@/frontend/hooks/api/use-list-staff'

export type AppointmentWarning = { title: string; description?: string } & (
  | { type: 'long-duration' }
  | { type: 'patient-needs-first-visit' }
  | { type: 'patient-appointment-type-not-allowed' }
  | { type: 'patient-has-no-subscription' }
  | { type: 'patient-has-unhealthy-subscription'; status: string }
  | { type: 'patient-has-overdue-invoices'; count: number }
  | { type: 'vet-not-scheduled' }
  | { type: 'outside-schedule-blocks' }
  | { type: 'type-not-allowed'; blockName: string }
  | { type: 'overlaps-appointment'; appointmentId: string }
  | { type: 'overlaps-blocked-slot'; blockedSlotId: string; note: string | null }
)

export interface AppointmentRequest {
  patientId?: string
  staffId?: string
  clinicId?: ClinicId
  date?: string
  startTime?: string
  endTime?: string
  appointmentTypeId?: AppointmentTypeId
  timeZone: string
  excludeAppointmentId?: string
}

interface AppointmentWarningState {
  warnings: AppointmentWarning[]
  isLoading: boolean
  isError: boolean
}

/** Builds a valid timezone-aware interval from the request's date and times. */
function getRequestInterval(request: AppointmentRequest | null) {
  if (!request?.date || !request.startTime || !request.endTime) return undefined

  const interval = wallClockInterval(
    { date: request.date, time: request.startTime },
    { date: request.date, time: request.endTime },
    request.timeZone
  )
  return interval.start < interval.end ? interval : undefined
}

/** Returns warnings based solely on the appointment's configured date and times. */
function getAppointmentConfigurationWarnings(
  request: AppointmentRequest | null
): AppointmentWarning[] {
  const interval = getRequestInterval(request)
  if (!interval || interval.end.getTime() - interval.start.getTime() <= 60 * 60 * 1000) return []

  return [
    {
      type: 'long-duration',
      title: 'This appointment is longer than 1 hour',
      description:
        'If you meant to block off a diary instead, please use the Blocked Slots feature.',
    },
  ]
}

/** Loads and evaluates booking eligibility for new appointments. */
function useEligibilityWarnings(
  request: AppointmentRequest | null,
  isNewAppointment: boolean
): AppointmentWarningState {
  const patientId = isNewAppointment ? request?.patientId : undefined
  const eligibilityQuery = useListPatientAppointmentTypes(patientId)

  if (!isNewAppointment || !patientId) {
    return { warnings: [], isLoading: false, isError: false }
  }

  const appointmentTypeIds = eligibilityQuery.data?.appointmentTypeIds
  const warning =
    appointmentTypeIds &&
    request?.appointmentTypeId &&
    !appointmentTypeIds.includes(request.appointmentTypeId)
      ? appointmentTypeIds.length === 1 && appointmentTypeIds[0] === 'first-visit'
        ? {
            type: 'patient-needs-first-visit' as const,
            title: 'This patient is currently only eligible for a first visit',
          }
        : {
            type: 'patient-appointment-type-not-allowed' as const,
            title: 'This patient is not eligible for this appointment type',
          }
      : undefined

  return {
    warnings: warning ? [warning] : [],
    isLoading: eligibilityQuery.isFetching,
    isError: eligibilityQuery.isError,
  }
}

/** Loads and evaluates overdue invoices for new appointments. */
function useBillingWarnings(
  request: AppointmentRequest | null,
  isNewAppointment: boolean
): AppointmentWarningState {
  const patientId = isNewAppointment ? request?.patientId : undefined
  const subscriptionQuery = useGetPatientSubscription(patientId)
  const invoicesQuery = useGetPatientInvoices(patientId)
  const subscription = subscriptionQuery.data
  const invoices = invoicesQuery.data
  const isFetching = subscriptionQuery.isFetching || invoicesQuery.isFetching
  const isError = subscriptionQuery.isError || invoicesQuery.isError

  if (!isNewAppointment || !patientId) {
    return { warnings: [], isLoading: false, isError: false }
  }

  if (!subscription || !invoices) {
    return { warnings: [], isLoading: isFetching, isError }
  }
  const warnings: AppointmentWarning[] = []
  if (subscription.stripeSubscriptionId === null) {
    warnings.push({
      type: 'patient-has-no-subscription',
      title: 'This patient has no subscription',
    })
  } else if (subscription.status && !HEALTHY_SUBSCRIPTION_STATUSES.has(subscription.status)) {
    warnings.push({
      type: 'patient-has-unhealthy-subscription',
      status: subscription.status,
      title: `This patient's subscription has a ${subscription.status.replaceAll('_', ' ')} status`,
    })
  }

  const overdueInvoiceCount = invoices.filter((invoice) => invoice.overdue).length
  if (overdueInvoiceCount) {
    warnings.push({
      type: 'patient-has-overdue-invoices',
      count: overdueInvoiceCount,
      title: `This patient has ${overdueInvoiceCount} overdue invoice${overdueInvoiceCount === 1 ? '' : 's'}`,
    })
  }

  return {
    warnings,
    isLoading: isFetching,
    isError,
  }
}

/** Loads and evaluates schedule, appointment, and blocked-slot availability for a vet. */
function useStaffAvailabilityWarnings(request: AppointmentRequest | null): AppointmentWarningState {
  const hasStaffDate = !!(request?.staffId && request.date)
  const hasScheduleInputs = hasStaffDate && !!request?.clinicId
  const staffQuery = useListStaff()
  const staffMember = staffQuery.data?.find((member) => member.id === request?.staffId)
  const to = hasStaffDate && request?.date ? addCalendarDays(request.date, 1) : undefined
  const staffIds = hasStaffDate && request?.staffId ? [request.staffId] : undefined
  const appointmentsQuery = useListAppointments({
    from: hasStaffDate ? request?.date : undefined,
    to,
    staffIds,
  })
  const blockedSlotsQuery = useListBlockedSlots({
    from: hasStaffDate ? request?.date : undefined,
    to,
    staffIds,
    clinicId: hasScheduleInputs ? request?.clinicId : undefined,
  })
  const scheduleQuery = useClinicSchedule({
    clinicId: hasScheduleInputs ? request?.clinicId : undefined,
    from: hasScheduleInputs ? request?.date : undefined,
    to: hasScheduleInputs ? to : undefined,
  })
  const schedule = useMemo(() => {
    if (!hasScheduleInputs || !staffMember || !scheduleQuery.data || !request?.date)
      return undefined
    const vetEntry = scheduleQuery.data
      .find((day) => day.date === request.date)
      ?.vets.find((vet) => vet.staff.email === staffMember.email)
    return vetEntry?.schedule ?? null
  }, [hasScheduleInputs, request?.date, scheduleQuery.data, staffMember])

  const interval = getRequestInterval(request)
  const warnings: AppointmentWarning[] = []
  if (interval && staffMember && request) {
    if (schedule === null) {
      warnings.push({
        type: 'vet-not-scheduled',
        title: 'This vet is not scheduled at this clinic on this day',
      })
    } else if (schedule) {
      const overlappingBlocks = schedule.filter((block) =>
        areIntervalsOverlapping(
          interval,
          wallClockInterval(
            { date: request.date!, time: block.startTime },
            { date: request.date!, time: block.endTime },
            request.timeZone
          )
        )
      )
      const coverage = schedule
        .map((block) => {
          const blockInterval = wallClockInterval(
            { date: request.date!, time: block.startTime },
            { date: request.date!, time: block.endTime },
            request.timeZone
          )
          return { start: blockInterval.start.getTime(), end: blockInterval.end.getTime() }
        })
        .sort((a, b) => a.start - b.start)

      // Advance across contiguous blocks to verify the appointment is fully covered.
      let frontier = interval.start.getTime()
      for (const segment of coverage) {
        if (segment.start > frontier) break
        frontier = Math.max(frontier, segment.end)
      }
      if (frontier < interval.end.getTime()) {
        warnings.push({
          type: 'outside-schedule-blocks',
          title: 'Overlaps with a non-working period',
        })
      }

      for (const block of overlappingBlocks) {
        const appointmentTypeId = request.appointmentTypeId
        const typeAllowed = block.appointmentTypesExcluded
          ? appointmentTypeId !== undefined &&
            !block.appointmentTypesExcluded.includes(appointmentTypeId)
          : appointmentTypeId === undefined ||
            block.appointmentTypesAllowed.length === 0 ||
            block.appointmentTypesAllowed.includes(appointmentTypeId)
        if (appointmentTypeId && !typeAllowed) {
          warnings.push({
            type: 'type-not-allowed',
            blockName: block.name,
            title: `Overlaps with a scheduling block (${block.name}) that does not permit ${APPOINTMENT_TYPES_BY_ID[appointmentTypeId]?.name ?? 'this appointment type'}`,
          })
        }
      }
    }

    const overlapClinicId = isLocumVet(staffMember.firstName) ? request?.clinicId : undefined
    for (const appointment of appointmentsQuery.data ?? []) {
      if (request?.excludeAppointmentId === appointment.id || appointment.status === 'cancelled')
        continue
      if (overlapClinicId && appointment.clinicId !== overlapClinicId) continue
      if (
        areIntervalsOverlapping(interval, {
          start: new Date(appointment.startTime),
          end: new Date(appointment.endTime),
        })
      ) {
        const title = [appointment.patient.name, appointment.appointmentTypeName]
          .filter(Boolean)
          .join(' — ')
        warnings.push({
          type: 'overlaps-appointment',
          appointmentId: appointment.id,
          title: `Overlaps with an existing appointment (${title})`,
        })
      }
    }

    for (const slot of blockedSlotsQuery.data ?? []) {
      if (overlapClinicId && slot.clinicId !== overlapClinicId) continue
      if (
        areIntervalsOverlapping(interval, {
          start: new Date(slot.startTime),
          end: new Date(slot.endTime),
        })
      ) {
        warnings.push({
          type: 'overlaps-blocked-slot',
          blockedSlotId: slot.id,
          note: slot.note,
          title: `Overlaps with a blocked slot${slot.note ? ` (${slot.note})` : ''}`,
        })
      }
    }
  }

  return {
    warnings,
    isLoading:
      (hasStaffDate && staffQuery.isFetching) ||
      (hasStaffDate && (appointmentsQuery.isFetching || blockedSlotsQuery.isFetching)) ||
      (hasScheduleInputs && scheduleQuery.isLoading),
    isError:
      (hasStaffDate && staffQuery.isError) ||
      (hasStaffDate && (appointmentsQuery.isError || blockedSlotsQuery.isError)) ||
      (hasScheduleInputs && scheduleQuery.isError),
  }
}

/** Loads appointment warnings as the corresponding request details become available. */
export function useAppointmentWarnings(
  request: AppointmentRequest | null,
  { isNewAppointment }: { isNewAppointment: boolean }
): AppointmentWarningState {
  const configurationWarnings = getAppointmentConfigurationWarnings(request)
  const eligibility = useEligibilityWarnings(request, isNewAppointment)
  const billing = useBillingWarnings(request, isNewAppointment)
  const staffAvailability = useStaffAvailabilityWarnings(request)

  return {
    warnings: [
      ...eligibility.warnings,
      ...billing.warnings,
      ...configurationWarnings,
      ...staffAvailability.warnings,
    ],
    isLoading: eligibility.isLoading || billing.isLoading || staffAvailability.isLoading,
    isError: eligibility.isError || billing.isError || staffAvailability.isError,
  }
}
